import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { IamSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import {
    CoreUIToastDirection,
    CoreUIToastV2Service,
} from '../../services/toast.service';
import {
    CreateRoleRequest,
    RoleDetail,
    RolesPage,
    UpdateRoleRequest,
    PermissionTree,
} from '../../../domain/models/session/roles.model';
import { RolesPort } from '../../../app/ports/iam/roles.port';
import { KLAB_IAM_ENV } from '../../config/micros-env.tokens';
import { EnvMicroKlamIam } from '../../config/env.micro.klam-iam';
import {
    mapSdkPaginationMetaToDomain,
    mapSdkPermissionTreeToDomain,
    mapSdkRoleDetailToDomain,
    mapSdkRoleSummaryToDomain,
} from '../../mappers/iam/roles.mapper';
import { rolesSdkMock } from '../../mocks/iam/roles.mock';
import { CrudMockAction } from '../../config/crud-mock-action.type';

type RolesMockAction = CrudMockAction | 'listPermissions';

@Injectable({ providedIn: 'root' })
export class RolesPortAdapter implements RolesPort {
    constructor(
        private rolesApi: IamSdk.RolesService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(KLAB_IAM_ENV) private env: EnvMicroKlamIam
    ) { }

    listRoles(page: number, size: number): Observable<RolesPage> {
        this.appEvents.loaderVisiblePublish(true, 'iam-roles-list');

        const source$ = this.shouldMock('getAll')
            ? of(rolesSdkMock.listRoles(page, size)).pipe(delay(360))
            : this.rolesApi.listRoles(page, size);

        return source$.pipe(
            map((resp: IamSdk.RoleListResponse) => ({
                items: (resp.content ?? []).map(mapSdkRoleSummaryToDomain),
                page: mapSdkPaginationMetaToDomain(resp.pagination),
            })),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'iam-roles-list'))
        );
    }

    getRoleById(roleId: number): Observable<RoleDetail> {
        this.appEvents.loaderVisiblePublish(true, 'iam-role-getById');

        const source$ = this.shouldMock('getById')
            ? of(rolesSdkMock.getRoleById(roleId)).pipe(delay(360))
            : this.rolesApi.getRoleById(roleId);

        return source$.pipe(
            map((resp: IamSdk.RoleDetailResponse) => mapSdkRoleDetailToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'iam-role-getById'))
        );
    }

    createRole(payload: CreateRoleRequest): Observable<RoleDetail> {
        this.appEvents.loaderVisiblePublish(true, 'iam-role-create');

        const sdkPayload: IamSdk.CreateRoleRequest = {
            name: payload.name,
            allowRead: payload.allowRead,
            allowCreate: payload.allowCreate,
            allowUpdate: payload.allowUpdate,
            allowDelete: payload.allowDelete,
            permissionIds: payload.permissionIds,
            scopes: (payload.scopes ?? []).map((s) => ({
                locationId: s.locationId,
                departmentId: s.departmentId,
            })),
        };

        const source$ = this.shouldMock('create')
            ? of(rolesSdkMock.createRole(sdkPayload)).pipe(delay(360))
            : this.rolesApi.createRole(sdkPayload);

        return source$.pipe(
            map((resp: IamSdk.RoleDetailResponse | null | undefined): RoleDetail => {
                if (resp != null && typeof resp === 'object' && resp.roleId != null) {
                    return mapSdkRoleDetailToDomain(resp);
                }
                return this.buildMinimalRoleDetailFromPayload(0, payload);
            }),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'iam-role-create'))
        );
    }

    updateRole(
        roleId: number,
        payload: UpdateRoleRequest
    ): Observable<RoleDetail> {
        this.appEvents.loaderVisiblePublish(true, 'iam-role-update');

        const sdkPayload: IamSdk.UpdateRoleRequest = {
            name: payload.name,
            allowRead: payload.allowRead,
            allowCreate: payload.allowCreate,
            allowUpdate: payload.allowUpdate,
            allowDelete: payload.allowDelete,
            permissionIds: payload.permissionIds,
            scopes: (payload.scopes ?? []).map((s) => ({
                locationId: s.locationId,
                departmentId: s.departmentId,
            })),
        };

        const source$ = this.shouldMock('update')
            ? of(rolesSdkMock.updateRole(roleId, sdkPayload)).pipe(delay(360))
            : this.rolesApi.updateRole(roleId, sdkPayload);

        return source$.pipe(
            map((resp: IamSdk.RoleDetailResponse | null | undefined): RoleDetail => {
                if (resp != null && typeof resp === 'object' && resp.roleId != null) {
                    return mapSdkRoleDetailToDomain(resp);
                }
                return this.buildMinimalRoleDetailFromPayload(roleId, payload);
            }),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'iam-role-update'))
        );
    }

    deleteRole(roleId: number): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'iam-role-delete');

        const source$ = this.shouldMock('delete')
            ? of(null)
                .pipe(delay(360))
                .pipe(
                    map(() => {
                        rolesSdkMock.deleteRole(roleId);
                        return void 0;
                    })
                )
            : this.rolesApi.deleteRole(roleId).pipe(map(() => void 0));

        return source$.pipe(
            catchError((err) => {
                if (err?.status !== 409) {
                    this.showErrorToast(err);
                }
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'iam-role-delete'))
        );
    }

    listPermissions(isEnabled?: boolean): Observable<PermissionTree> {
        this.appEvents.loaderVisiblePublish(true, 'iam-permissions-list');

        const source$ = this.shouldMock('listPermissions')
            ? of(rolesSdkMock.listPermissions(isEnabled)).pipe(delay(360))
            : this.rolesApi.listPermissions(isEnabled);

        return source$.pipe(
            map((resp: IamSdk.PermissionTreeResponse) =>
                mapSdkPermissionTreeToDomain(resp)
            ),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() =>
                this.appEvents.loaderVisiblePublish(false, 'iam-permissions-list')
            )
        );
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }

    private shouldMock(action: RolesMockAction): boolean {
        if (!this.env) return false;
        if (this.env.useMocks === true) return true;
        const rolesMock = this.env.mock?.roles;
        if (!rolesMock) return false;
        return rolesMock[action] === true;
    }

    private buildMinimalRoleDetailFromPayload(
        id: number,
        payload: CreateRoleRequest
    ): RoleDetail {
        const now = new Date().toISOString();
        return {
            id,
            name: payload.name ?? '',
            isEnabled: true,
            allowRead: payload.allowRead ?? false,
            allowCreate: payload.allowCreate ?? false,
            allowUpdate: payload.allowUpdate ?? false,
            allowDelete: payload.allowDelete ?? false,
            locations: [],
            departments: [],
            createdAt: now,
            permissions: [],
            scopes: payload.scopes ?? [],
            updatedAt: undefined,
        };
    }
}
